// **The production bootstrap** (rounds-slice implementation-review **F1**; previously the body of
// `main.tsx`).
//
// The server has exactly one mount-time fork (design **D1**): under `GameMode::Live` it serves
// `/ws` plus the lobby routes and no rounds routes; under `GameMode::Rounds` it serves the rounds
// routes and no `/ws` at all. The client had no matching fork — `main.tsx` unconditionally started
// `SessionController` over a `/ws` transport — so a Rounds deployment (which is the **default**
// mode: `server-config`'s `mode` defaults to `Rounds`) could not mint a sandbox, press Run, or
// review a trace. This module is the client's half of that fork.
//
// It is a module, not inline `main.tsx` code, for one reason: `main.tsx` was untestable, and F1's
// whole finding was that production composition had no test. `Bootstrap` takes **environment**
// seams only — `fetch`, storage, the transport factory, the renderer factory, a scheduler/RNG — all
// defaulting to the real thing. It takes no route props: every seam `AppRoutes` receives in either
// mode is derived inside the mode's own composition root (`SessionController` / `RoundsController`),
// which is exactly the code path a browser takes. `bootstrap.rounds.test.tsx` drives it.
//
// `main.tsx` is now this module's caller and nothing else.

import { StrictMode, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import type { InMatchRenderer } from './InMatchCanvas';
import { createOnboardingSeenStore } from './onboarding-store';
import type { OnboardingStorageLike } from './onboarding-store';
import type { SessionStore } from './SessionController';
import { createSessionStorageStore } from './session-store';
import { RoundsApp } from './rounds/RoundsApp';
import { resolveBootSurface, type GameMode } from './rounds/game-mode';
import { PresentationDemoRoot } from './demo/PresentationDemoRoot';
import type { Cue } from './demo/cues';
import { PresentationPlayerRoot } from './presentation/PresentationPlayerRoot';
import type { StorageLike } from './rounds/rounds-credential-store';
import type { PracticeEnvironment } from './rounds/RoundsController';
import { productionPracticeEnvironment } from './rounds/practice-environment';
import { CanvasRenderer } from '../canvas/renderer';
import type { Clock } from '../canvas/frame-scheduler';
import type { Rng, Scheduler, TransportFactory } from '../net/wire/connection';
import { createWebSocketTransportFactory } from '../net/wire/ws-transport';
import { useClientStore } from '../state/store';
import { resolveIdentity, type IdentityState } from '../net/auth-identity';
import { INERT_ACQUIRER, withBearerToken, type TokenAcquirer } from '../net/auth-token';
import { createMsalAcquirer } from '../net/msal-acquirer';

const SESSION_STORAGE_KEY = 'waggle-session';

/**
 * Everything the bootstrap needs from its environment. Every field is optional and defaults to the
 * real browser/production value; a test replaces only what a jsdom process cannot provide (a
 * server to `fetch`, durable storage, a GPU render target, a WebSocket).
 *
 * Note what is absent, deliberately: **no route seams**. `colonyRun`, `roundsReview`,
 * `colonyController`, `lod` and friends are derived by whichever composition root the resolved mode
 * selects, so a test that drives this module drives the real derivation.
 */
export interface BootstrapDeps {
  /** The API origin. `''`/undefined means same-origin, which is production. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /**
   * `window.location.pathname`, for the `/demo` branch (design **DD1**). Resolved **ahead** of
   * mode resolution: when it is `/demo` the demo composition root mounts and neither the mode
   * probe nor either mode's root is ever reached — the probe is a network round trip that can
   * decide nothing the pathname has already decided.
   */
  pathname?: string;
  /** Presentation cue-list override for production-root behavior tests and rehearsals. */
  demoCueList?: readonly Cue[];
  /** Durable (`localStorage`-scoped) storage: the rounds sandbox credential. */
  storage?: StorageLike;
  /** Tab-scoped (`sessionStorage`) live-match session persistence (design DD8). */
  sessionStore?: SessionStore;
  /** `window.location.search`, for the `?mode=` override. */
  search?: string;
  /** The build-time `VITE_GAME_MODE`. */
  configuredMode?: string | undefined;
  /** Skips mode resolution entirely (a test that only cares about one mode). */
  mode?: GameMode;
  transportFactory?: TransportFactory;
  scheduler?: Scheduler;
  /** Wall-clock source. Live mode has no use for one; the rounds root counts the advisory prompt
   * cooldown down with it (implementation-review **F3**), so a test can spend a 60-second cooldown
   * without spending 60 seconds. Defaults to `performance.now`. */
  clock?: Clock;
  rng?: Rng;
  createRenderer?: (container: HTMLElement, tickRateHz: number) => InMatchRenderer;
  /** The rounds-mode display name to mint under on a first visit. */
  displayName?: string;
  /**
   * The **practice path's** environment seams (`client-app`), owned by the rounds composition root
   * separately from the tournament path's — a distinct credential store, the device capability gate,
   * the simulation worker factory and the local trace store.
   *
   * Environment, in the same sense every field above is: a jsdom process has no OPFS, no worker and
   * no wasm module, so a test replaces exactly those and lets the root derive everything else. No
   * route seam is added here and none may be — a practice run's request accounting is only
   * meaningful if the requests are the ones production composition makes.
   */
  practice?: PracticeEnvironment;
  /**
   * **The authentication identity route seam, with an inert default** (`client-app`).
   *
   * Injected, this skips resolution entirely — the `deps.mode` convention one field up. Omitted,
   * the rounds root resolves it from the server in the background and publishes the answer, and
   * the live root makes no request at all.
   */
  identity?: IdentityState;
  /** Overrides the identity resolver itself, for a test that wants to observe the request or fail
   * it deliberately. Production is `resolveIdentity`. */
  resolveIdentityImpl?: typeof resolveIdentity;
  /**
   * **The token-acquisition seam** (`client-app`, design **D16**).
   *
   * MSAL, injected exactly as every other route seam is, so the bootstrap's behaviour on each
   * identity state is assertable with no tenant, no network and no browser redirect. Omitted, the
   * root builds the real one from the build-time tenant configuration — and a bundle built for no
   * tenant gets [`INERT_ACQUIRER`], which acquires nothing and loads no MSAL.
   */
  tokenAcquirer?: TokenAcquirer;
}

/** The same-origin WebSocket URL: same host, `ws:`/`wss:` mirroring the page's own `http:`/
 * `https:` (mirrors the lobby's same-origin root-relative default). */
function productionWsUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

/** The real live `SessionStore` (design DD8): `{token,matchId,role,phase,isHost}` persists across
 * a page reload by round-tripping through `window.sessionStorage` as JSON. The parsing, **runtime
 * validation**, and clearing all live in the shared `createSessionStorageStore` adapter, so a
 * missing/malformed/foreign/legacy value — or storage unavailable entirely (private browsing,
 * quota, a sandboxed `SecurityError`) — is treated as "nothing persisted" (and any poison value
 * dropped) rather than throwing or installing a record that matches no D9 classification; a reload
 * then simply starts fresh at the Lobby. `window.sessionStorage` is accessed lazily inside the
 * passed shim (never at factory time) so even a throwing property access degrades gracefully. */
function windowSessionStore(): SessionStore {
  return createSessionStorageStore(
    {
      getItem: (key) => window.sessionStorage.getItem(key),
      setItem: (key, value) => window.sessionStorage.setItem(key, value),
      removeItem: (key) => window.sessionStorage.removeItem(key),
    },
    SESSION_STORAGE_KEY,
  );
}

/** The real once-per-browser onboarding "seen" adapter (onboarding design D-Persist): the versioned
 * `waggle-onboarding-seen` record persists across page reload AND browser close via
 * `window.localStorage` (unlike the tab-scoped `waggle-session` `sessionStorage`).
 * `window.localStorage` is accessed lazily inside the passed shim (never at factory time), and
 * every access is guarded inside the adapter, so private-mode / quota / a sandboxed
 * `SecurityError` degrades to "not seen" rather than throwing at boot. */
function windowOnboardingSeenStore(): ReturnType<typeof createOnboardingSeenStore> {
  const shim: OnboardingStorageLike = {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
    removeItem: (key) => window.localStorage.removeItem(key),
  };
  return createOnboardingSeenStore(shim);
}

const wallClockScheduler: Scheduler = {
  setTimer: (fn, ms) => window.setTimeout(fn, ms) as unknown as number,
  clearTimer: (id) => window.clearTimeout(id),
};

const mathRandomRng: Rng = { next: () => Math.random() };

/** The one production renderer factory, shared by both modes. Deliberately returns the
 * CONCRETE `CanvasRenderer` rather than the narrower `InMatchRenderer`: the rounds review
 * frame's `ReviewRenderer` seam is a superset by one parameter (the wide-tier colony
 * colouring context), and annotating the narrower type here would let a factory that
 * silently ignores it satisfy the check. */
function createProductionRenderer(container: HTMLElement, tickRateHz: number): CanvasRenderer {
  return new CanvasRenderer(container, tickRateHz);
}

/**
 * Resolves the mode, then mounts that mode's composition root.
 *
 * The live branch is bit-for-bit the tree `main.tsx` rendered before this change — same `App`, same
 * production `WebSocket` transport factory, same `sessionStorage` store, same wall-clock
 * scheduler/RNG, same PixiJS renderer factory — because the live boot path is the one thing this
 * fork must not disturb. Only the *choice* is new.
 */
export function Bootstrap(deps: BootstrapDeps = {}): ReactElement | null {
  // DD1: resolved ahead of everything else. `/demo` is a third composition root, not a mode —
  // no probe, no mode state, no live/rounds root construction of any kind.
  const pathname = deps.pathname ?? window.location.pathname;
  const isDemo = pathname === '/demo';
  const presentationTeam = pathname === '/demo/red' ? 'red' : pathname === '/demo/blue' ? 'blue' : null;

  const [mode, setMode] = useState<GameMode | null>(deps.mode ?? null);
  // The pvp prototype's discovery flag (`docs/dev/pvp-prototype.md`), resolved on the SAME probe
  // as the mode — one request, two readings. `false` whenever the probe is skipped or inconclusive,
  // which is every shipped deployment.
  const [pvp, setPvp] = useState(false);

  /**
   * **The practice path's production seams** (task 5.12).
   *
   * Same shape as `createRenderer={deps.createRenderer ?? createProductionRenderer}` one screen down,
   * and for the same stated reason: production composition is the code path a browser takes, and a
   * seam only the tests supply is a seam production never gets. Until this line existed, `main.tsx`
   * passed no `practice` at all, so `RoundsController`'s `createWorker`/`compile` were `undefined`, its
   * practice `run` seam was `null`, and Home's "Play in sandbox" CTA reached a sandbox that could not
   * run anything — with every gate green, because the only `PracticeEngine` in the repo was a double.
   *
   * Merged field-by-field inside `productionPracticeEnvironment`, so a test that replaces only what a
   * jsdom process cannot provide still exercises the real remainder.
   *
   * Memoized because the environment holds a memoized module load: rebuilt per render it would attempt
   * one `waggle-wasm` load per render.
   */
  const practice = useMemo(
    () => productionPracticeEnvironment(deps.practice),
    // `deps` is a DI bundle injected once by the caller, not reactive props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * **The identity state, resolved in the background and published when it arrives**
   * (`client-app`).
   *
   * `null` is "not yet", and it is deliberately *not* a state screens ever see: `RoundsController`
   * substitutes `unresolved`, which is what every screen renders its ungated fallback for. So
   * resolution **never blocks first paint** — Home paints its join affordance immediately and
   * re-renders when the answer lands. That matters more here than for the credential restore,
   * because this state decides the join form's *shape* rather than a CTA's label.
   */
  const [identity, setIdentity] = useState<IdentityState | null>(deps.identity ?? null);

  /**
   * **The token acquirer, owned by the root and by nothing below it** (`client-app`).
   *
   * A promise rather than a value: the production one loads MSAL dynamically, and the HTTP layer
   * below must exist before that resolves or first paint would wait on it. A bundle built for no
   * tenant resolves to the inert acquirer without loading MSAL at all.
   */
  const acquirer = useMemo<Promise<TokenAcquirer>>(
    () =>
      deps.tokenAcquirer !== undefined
        ? Promise.resolve(deps.tokenAcquirer)
        : // Never rejects: a failure to construct MSAL leaves the client acquiring nothing, which
          // is the same state a bundle with no tenant is in, rather than an unhandled rejection at
          // boot.
          createMsalAcquirer().catch(() => INERT_ACQUIRER),
    // `deps` is a DI bundle injected once by the caller, not reactive props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * **The bearer token, attached and recovered once at the root** (`client-app`, design **D21**).
   *
   * Wrapping the root's own `fetchImpl` is what makes the rule live in *one* place: every rounds
   * client below takes its fetch from here, so a route added later inherits both the credential
   * and the `401` recovery with no call site to remember. A `deps.fetchImpl` a test injects is
   * wrapped too, deliberately — the production behaviour is the thing under test.
   *
   * Declared ahead of the identity effect below (rather than where D21 first landed it, after):
   * the effect's post-acquisition re-resolve must go through this, not through the raw
   * `deps.fetchImpl`, or the freshly acquired token never reaches the server and `authenticated`
   * is unreachable — reading in dependency order keeps that visible instead of relying on closure
   * hoisting.
   */
  const gatedFetch = useMemo(
    () =>
      withBearerToken(deps.fetchImpl ?? globalThis.fetch.bind(globalThis), {
        acquirer: {
          acquireSilent: async () => (await acquirer).acquireSilent(),
          signIn: async () => (await acquirer).signIn(),
          signOut: async () => (await acquirer).signOut(),
        },
        // A freshly acquired token the server still refuses. Retrying again is the loop the rule
        // exists to make impossible, so the identity state goes to `required` — Home's sign-in
        // affordance, which is at least actionable.
        onSignedOut: () => setIdentity({ kind: 'required' }),
      }),
    // `deps` is a DI bundle injected once by the caller, not reactive props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    // **Under `Live` the root makes no such request and acquires no token.** Live mode has no Home
    // screen and no gated join form, so a boot-time fetch there would be an unused request on a
    // surface this change does not touch.
    if (deps.identity !== undefined || mode !== 'rounds') {
      return undefined;
    }
    let live = true;
    const resolve = deps.resolveIdentityImpl ?? resolveIdentity;
    // The FIRST resolve deliberately stays on the raw `deps.fetchImpl`, not `gatedFetch`: its whole
    // job is to learn whether a token is even wanted (`disabled`/`required`/`authenticated`) before
    // any is acquired, and `gatedFetch` acquires one unconditionally on its first use. Routing this
    // call through it would force a silent-acquisition attempt — and, for a bundle built against a
    // tenant on a server that has the gate off, an MSAL round trip — on every boot, contradicting
    // "no credential read at all" for a disabled deployment (DESIGN.md §15).
    void resolve({ baseUrl: deps.baseUrl, fetchImpl: deps.fetchImpl }).then((resolved) => {
      if (!live) {
        return;
      }
      setIdentity(resolved);
      if (resolved.kind === 'required') {
        // **Required and not signed in is actionable, and `unresolved` is not** — which is the
        // whole reason the two are distinct states. A *silent* attempt first, so a returning
        // player never sees the sign-in affordance at all; if it succeeds the identity is
        // re-resolved and Home re-renders as the gated form.
        void acquirer.then(async (msal) => {
          const token = await msal.acquireSilent();
          if (token === null || !live) {
            return;
          }
          // Through `gatedFetch`, not `deps.fetchImpl`: the token just acquired has to reach the
          // server on THIS request, and only the bearer-decorated fetch attaches it. The raw fetch
          // carries no `Authorization` header at all, so the server can only ever answer
          // `required` again — `authenticated` would be unreachable no matter how many tokens the
          // client acquired.
          const again = await resolve({ baseUrl: deps.baseUrl, fetchImpl: gatedFetch });
          if (live) {
            setIdentity(again);
          }
        });
      }
    });
    return () => {
      live = false;
    };
    // `deps` is a DI bundle injected once by the caller, not reactive props; `mode` is not, and
    // gates the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Guards the probe the same way `RoundsController`'s boot effect guards its restore mint
  // (`bootedRef`/`liveRef`) and `SessionController` guards `connect()`: `StrictMode` double-invokes
  // this effect (setup → cleanup → setup) synchronously on the initial commit, and unlike a mint, a
  // probe has no server-side idempotency to fall back on — an unguarded probe fired the real
  // `GET /rounds/session` twice on every boot, each logged by the browser as a failed load
  // regardless of the 405/404 being an expected, positive signal (see `game-mode.ts`).
  //
  // `liveRef` is a **ref shared across both invocations**, not a `let live` closed over by each
  // effect body individually — that distinction is load-bearing. The probe is only ever launched by
  // the *first* invocation; the second sees `probedRef.current` already true and skips launching a
  // second one. If liveness were a per-invocation closure variable, the first invocation's own
  // cleanup (run synchronously by StrictMode before the second setup) would freeze its closure's
  // `live` at `false` forever, so the in-flight probe's `.then` would never apply — and the second
  // invocation, having launched nothing, would have nothing to apply either. Boot would hang on
  // `mode === null`. A shared ref instead reads whatever the *current* mount state is when the
  // promise resolves: `false` only after a genuine final unmount.
  const probedRef = useRef(false);
  const liveRef = useRef(true);
  useEffect(() => {
    if (isDemo || presentationTeam !== null || deps.mode !== undefined) {
      return undefined;
    }
    liveRef.current = true;
    if (probedRef.current) {
      return () => {
        liveRef.current = false;
      };
    }
    probedRef.current = true;
    void resolveBootSurface({
      baseUrl: deps.baseUrl,
      fetchImpl: deps.fetchImpl,
      search: deps.search,
      configured: deps.configuredMode,
    }).then((resolved) => {
      if (liveRef.current) {
        setMode(resolved.mode);
        setPvp(resolved.pvp);
      }
    });
    return () => {
      liveRef.current = false;
    };
    // `deps` is a DI bundle injected once by the caller, not reactive props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (presentationTeam !== null) {
    return (
      <PresentationPlayerRoot
        team={presentationTeam}
        baseUrl={deps.baseUrl}
        fetchImpl={gatedFetch}
        storage={deps.storage}
        sessionStore={deps.sessionStore ?? windowSessionStore()}
        transportFactory={deps.transportFactory ?? createWebSocketTransportFactory(productionWsUrl())}
        scheduler={deps.scheduler ?? wallClockScheduler}
        rng={deps.rng ?? mathRandomRng}
        createRenderer={deps.createRenderer ?? createProductionRenderer}
      />
    );
  }

  if (isDemo) {
    // The demo's own composition root — the host membership credential, the worker-backed stage and
    // the shipped renderer (`DemoRoot.tsx`). It takes the same environment seams every other branch
    // does and constructs everything else itself.
    //
    // **Through `gatedFetch`, not the raw `deps.fetchImpl`.** `/demo/instruct` and every `/rounds`
    // route the presentation drives are under `API_PREFIXES`, so a gated deployment refuses them
    // without a tenant token — and on the raw fetch there was none to send, which made the whole
    // presentation unrunnable behind the gate. On an ungated deployment the wrapper acquires nothing
    // and adds no header, so this costs that posture a token it never had.
    return (
      <PresentationDemoRoot
        baseUrl={deps.baseUrl}
        fetchImpl={gatedFetch}
        createRenderer={deps.createRenderer ?? createProductionRenderer}
        transportFactory={deps.transportFactory ?? createWebSocketTransportFactory(productionWsUrl())}
        scheduler={deps.scheduler ?? wallClockScheduler}
        rng={deps.rng ?? mathRandomRng}
        cueList={deps.demoCueList}
      />
    );
  }

  if (mode === null) {
    // One same-origin probe's worth of nothing — the same blank the page shows before React mounts.
    return null;
  }
  if (mode === 'rounds') {
    return (
      <RoundsApp
        baseUrl={deps.baseUrl}
        fetchImpl={gatedFetch}
        identity={identity ?? { kind: 'unresolved' }}
        // Home renders the controls; the root owns what they do. Both are deliberately
        // fire-and-forget: in a browser each one navigates, so there is nothing to await.
        signIn={() => {
          void acquirer.then((msal) => msal.signIn());
        }}
        signOut={() => {
          void acquirer.then((msal) => msal.signOut());
        }}
        storage={deps.storage}
        displayName={deps.displayName}
        clock={deps.clock}
        scheduler={deps.scheduler}
        practice={practice}
        // The review screen's frame draws through the same PixiJS renderer the live
        // Colony/Match canvases use (D-156). Threaded here for the same reason the live
        // branch threads it: production composition is the code path a browser takes, and
        // a seam only the tests supply is a seam production never gets.
        createRenderer={deps.createRenderer ?? createProductionRenderer}
      />
    );
  }
  return (
    <App
      pvp={pvp}
      // `lobby.baseUrl` is deliberately NOT derived from `deps.baseUrl`: the live lobby's
      // same-origin root-relative base ('' — so a lobby call composes to `/matches`, the path the
      // server actually mounts) is `SessionController`'s own default and stays exactly that.
      // Only `fetchImpl` is threaded, and in production it is `undefined` — which is what
      // `LobbyClient` already defaults to the global `fetch` for, so the live path is unchanged.
      lobby={{
        fetchImpl: gatedFetch,
        opponent:
          new URLSearchParams(deps.search ?? window.location.search).get('opponent') === 'human'
            ? 'human'
            : 'auto',
      }}
      transportFactory={deps.transportFactory ?? createWebSocketTransportFactory(productionWsUrl())}
      sessionStore={deps.sessionStore ?? windowSessionStore()}
      scheduler={deps.scheduler ?? wallClockScheduler}
      rng={deps.rng ?? mathRandomRng}
      createRenderer={deps.createRenderer ?? createProductionRenderer}
    />
  );
}

/**
 * Mounts the app into `root`.
 *
 * `StrictMode` stays on (React double-invokes mount effects in dev, catching exactly the class of
 * bug DD9 closes: `SessionController` guards `connect()` against it, and `RoundsController` guards
 * minting the same way — see their tests).
 */
export function mountApp(root: HTMLElement, deps: BootstrapDeps = {}): void {
  // Seed the durable onboarding "seen" mirror from `localStorage` before first render (onboarding
  // design D-Slice): the FirstRunGuide gate reads `hasSeenColonyIntro`, whose sole source of truth
  // is the versioned `waggle-onboarding-seen` record. Guarded inside the adapter, so a throwing
  // `localStorage` (private mode) degrades to "not seen" (fail-open to showing the guide).
  // New-match re-seeding happens at the Colony gate; `reset()` / `resetForNewMatch()` are left
  // unchanged.
  useClientStore.getState().setHasSeenColonyIntro(windowOnboardingSeenStore().hasSeenGuide());

  createRoot(root).render(
    <StrictMode>
      <Bootstrap {...deps} />
    </StrictMode>,
  );
}
